import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/Claims.css";
//
/* ════════════════════════════════════════════════════════════
   EXPENSE HEADS  — numeric id must match ExpenseHeadId in DB
════════════════════════════════════════════════════════════ */
const HEADS = [
  { id: 1, name: "Fuel / Diesel",    ico: "⛽" },
  { id: 2, name: "Toll / Octroi",    ico: "🛣️" },
  { id: 3, name: "Hamali / Loading", ico: "💪" },
  { id: 4, name: "Conveyance",       ico: "🏍️" },
  { id: 5, name: "Parking",          ico: "🅿️" },
  { id: 6, name: "Food / Halt",      ico: "🍽️" },
  { id: 7, name: "Detention",        ico: "⏱️" },
  { id: 8, name: "Miscellaneous",    ico: "📋" },
];

/* ════════ MY CLAIMS FILTER BAR STYLES ════════
   Inline (rather than added to Claims.css) for the same reason as the
   banner selects above — no CSS file to edit. Move to classes if you'd
   rather keep styling out of the JSX. */
const filtersBarStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  alignItems: "flex-end",
  background: "#fff",
  border: "1px solid #e6e8ee",
  borderRadius: 12,
  padding: "12px 14px",
  marginBottom: 12,
};
const filterFieldStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  minWidth: 150,
  flex: "1 1 150px",
};
const filterLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#8a92a6",
  textTransform: "uppercase",
  letterSpacing: 0.3,
};
const filterSelectStyle = {
  width: "100%",
  background: "#f7f8fb",
  border: "1px solid #e0e3ea",
  borderRadius: 8,
  color: "#1c2540",
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 10px",
  outline: "none",
};
const clearFilterBtnStyle = {
  background: "#fdeeee",
  color: "#c0392b",
  border: "1px solid #f3c7c7",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  padding: "8px 12px",
  cursor: "pointer",
  height: 37,
};

/* ════════════════════════════════════════════════════════════
   AUTOCOMPLETE  — same search-as-you-type dropdown used for
   "Party" on ConsignmentBooking.jsx, ported here for the
   Branch / Delivery Boy pickers. `variant` switches between the
   dark navy banner theme and the light filter-bar theme so it
   drops into both spots without extra props.
════════════════════════════════════════════════════════════ */
function AutoComplete({
  options,
  value,
  onChange,
  placeholder = "Search…",
  inputId,
  disabled = false,
  variant = "light", // "light" | "dark"
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const wrapRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else setQuery("");
  }, [value, selected]);

  const filtered = options.filter(
    (o) => o.value !== undefined && o.label != null &&
      String(o.label).toLowerCase().includes((query || "").toLowerCase())
  );

  useEffect(() => {
    if (focused >= 0 && optionRefs.current[focused]) {
      try {
        optionRefs.current[focused].scrollIntoView({ block: "nearest" });
      } catch (err) {
        // scrolling is a nice-to-have only
      }
    }
  }, [focused]);

  function hl(text, q) {
    if (!q) return text;
    const i = String(text).toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
  }

  function choose(opt) {
    onChange(String(opt.value));
    setQuery(opt.label);
    setIsOpen(false);
    setFocused(-1);
    setCursorMoved(false);
  }

  function clear() {
    onChange("");
    setQuery("");
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  function handleKeyDown(e) {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
        setCursorMoved(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.min(f + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (cursorMoved && filtered.length > 0) {
        const target = filtered[focused] || filtered[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
    }
  }

  return (
    <div className={`clm-ac clm-ac--${variant}${disabled ? " clm-ac--disabled" : ""}`} ref={wrapRef}>
      <div className={`clm-ac-box${isOpen ? " open" : ""}`}>
        <input
          id={inputId}
          className="clm-ac-in"
          type="text"
          autoComplete="off"
          placeholder={disabled ? "Loading…" : placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => { if (!disabled) { setIsOpen(true); setCursorMoved(false); } }}
          onClick={() => { if (!disabled && !isOpen) setIsOpen(true); }}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && !disabled && (
          <span className="clm-ac-x on" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
        )}
        {!disabled && (
          <span className="clm-ac-chev" onMouseDown={(e) => { e.preventDefault(); setIsOpen((o) => !o); }}>▾</span>
        )}
      </div>
      {isOpen && !disabled && (
        <div className="clm-ac-drop open">
          {filtered.length === 0 ? (
            <div className="clm-ac-empty">No matches found</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                ref={(el) => { optionRefs.current[i] = el; }}
                className={`clm-ac-opt${String(opt.value) === String(value) ? " sel" : ""}${i === focused ? " foc" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                onMouseEnter={() => setFocused(i)}
              >
                {hl(opt.label, query)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/* Injected once (see ExpClaims) so the two clm-ac variants above don't
   need a Claims.css edit — dark matches the navy claim banner, light
   matches the filter bar's white/grey inputs. */
const AUTOCOMPLETE_STYLES = `
.clm-ac { position: relative; width: 100%; }
.clm-ac-box { position: relative; display: flex; align-items: center; }
.clm-ac-in {
  width: 100%;
  border-radius: 8px;
  outline: none;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 46px 8px 10px;
  box-sizing: border-box;
}
.clm-ac--light .clm-ac-in {
  background: #f7f8fb;
  border: 1px solid #e0e3ea;
  color: #1c2540;
}
.clm-ac--light .clm-ac-box.open .clm-ac-in { border-color: #98a4c7; }
.clm-ac--dark .clm-ac-in {
  background: rgba(255,255,255,0.12);
  border: 1px solid rgba(255,255,255,0.25);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}
.clm-ac--dark .clm-ac-in::placeholder { color: rgba(255,255,255,0.65); }
.clm-ac--disabled .clm-ac-in { cursor: not-allowed; opacity: 0.7; }
.clm-ac-x, .clm-ac-chev {
  position: absolute;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  line-height: 1;
}
.clm-ac-chev { right: 10px; opacity: 0.6; }
.clm-ac-x { right: 26px; opacity: 0.6; font-size: 15px; }
.clm-ac--dark .clm-ac-x, .clm-ac--dark .clm-ac-chev { color: #fff; }
.clm-ac--light .clm-ac-x, .clm-ac--light .clm-ac-chev { color: #1c2540; }
.clm-ac-drop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 40;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e0e3ea;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 27, 61, 0.16);
}
.clm-ac-opt {
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #1c2540;
  cursor: pointer;
  text-align: left !important;
}
.clm-ac-empty { text-align: left !important; }
.clm-ac-opt mark { background: #ffe58a; color: inherit; border-radius: 2px; }
.clm-ac-opt:hover, .clm-ac-opt.foc { background: #f0f2fa; }
.clm-ac-opt.sel { background: #e8ecff; }
.clm-ac-empty { padding: 10px 12px; font-size: 13px; color: #8a92a6; }
`;

/* ════════ UTILITIES ════════ */
function fmt(n) {
  return (Math.round(n * 100) / 100)
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function fmtK(n) {
  return n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "K" : String(n);
}
function numWords(n) {
  if (n === 0) return "Zero Rupees Only";
  const o = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
    "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const t = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  function w(n) {
    if (n === 0) return "";
    if (n < 20) return o[n] + " ";
    if (n < 100) return t[Math.floor(n/10)] + " " + (n%10 ? o[n%10]+" " : "");
    if (n < 1000) return o[Math.floor(n/100)] + " Hundred " + (n%100 ? w(n%100) : "");
    if (n < 100000) return w(Math.floor(n/1000)) + "Thousand " + (n%1000 ? w(n%1000) : "");
    if (n < 10000000) return w(Math.floor(n/100000)) + "Lakh " + (n%100000 ? w(n%100000) : "");
    return w(Math.floor(n/10000000)) + "Crore " + (n%10000000 ? w(n%10000000) : "");
  }
  return w(n).trim() + " Rupees Only";
}
function todayISO() {
  return new Date().toISOString().split("T")[0];
}
function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" });
}

/* ════════ TOAST (portal — same as Branch.jsx) ════════ */
function Toast({ toast }) {
  if (!toast.show) return null;
  return createPortal(
    <div className={`clm-toast-portal ${toast.type || ""}`} style={{ zIndex: 99999 }}>
      <span>{toast.msg}</span>
    </div>,
    document.body
  );
}

/* ════════ DELETE OVERLAY (portal) ════════ */
function DeleteOverlay({ pending, onConfirm, onClose }) {
  if (!pending) return null;
  return createPortal(
    <div className="clm-del-overlay" style={{ zIndex: 10000 }}>
      <div className="clm-del-modal">
        <div className="clm-del-icon">⚠️</div>
        <h3>Remove Consignment?</h3>
        <p>{pending.msg}</p>
        <div className="clm-del-actions">
          <button className="clm-btn clm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="clm-btn clm-btn-danger" onClick={onConfirm}>Remove</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ════════ SUBMIT CONFIRM SHEET (portal) ════════ */
function SubmitSheet({ total, onConfirm, onClose }) {
  return createPortal(
    <div className="clm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="clm-sheet">
        <div className="clm-sheet-handle" />
        <div className="clm-sheet-title">Submit for Approval?</div>
        <div className="clm-sheet-sub">
          This claim will be sent to your branch manager for review.
          You cannot edit it after submitting.
        </div>
        <div className="clm-sheet-total">
          <div className="clm-sheet-total-lbl">Total Claim Amount</div>
          <div className="clm-sheet-total-val">₹ {fmt(total)}</div>
        </div>
        <div className="clm-sheet-btns">
          <button className="clm-btn clm-btn-ghost clm-btn-block" onClick={onClose}>Cancel</button>
          <button className="clm-btn clm-btn-green clm-btn-block" onClick={onConfirm}>
            ✓ Confirm &amp; Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ════════ SUCCESS POPUP (portal) ════════ */
function SuccessPopup({ claimNo, onClose }) {
  if (!claimNo) return null;
  return createPortal(
    <div className="clm-overlay" style={{ zIndex: 10001 }}>
      <div className="clm-success-popup">
        <div className="clm-success-icon">✅</div>
        <div className="clm-success-title">Claim Submitted!</div>
        <div className="clm-success-sub">Your expense claim has been submitted for approval.</div>
        <div className="clm-success-no-label">Claim Number</div>
        <div className="clm-success-no">{claimNo}</div>
        <button className="clm-btn clm-btn-green clm-btn-block" style={{ marginTop: 20 }} onClick={onClose}>
          OK
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ════════ ALREADY CLAIMED POPUP (portal) ════════ */
function AlreadyClaimedPopup({ info, onClose }) {
  if (!info) return null;
  const statusClass = {
    pending:  "clm-pill-pending",
    approved: "clm-pill-approved",
    rejected: "clm-pill-rejected",
    paid:     "clm-pill-paid",
    draft:    "clm-pill-draft",
  }[(info.status ?? "").toLowerCase()] ?? "clm-pill-draft";

  return createPortal(
    <div className="clm-overlay" style={{ alignItems: "center", zIndex: 10002 }}>
      <div className="clm-already-popup">
        <div className="clm-ap-icon">⚠️</div>
        <div className="clm-ap-title">Already Claimed</div>
        <div className="clm-ap-sub">
          Consignment <strong>{info.cnr}</strong> has already been submitted in a previous claim.
        </div>
        <div className="clm-ap-info-grid">
          <div className="clm-ap-info-item">
            <span className="clm-ap-info-lbl">Claim No</span>
            <span className="clm-ap-info-val clm-ap-claimno">{info.claimNo ?? "—"}</span>
          </div>
          <div className="clm-ap-info-item">
            <span className="clm-ap-info-lbl">Date</span>
            <span className="clm-ap-info-val">{info.claimDate ? info.claimDate.slice(0, 10) : "—"}</span>
          </div>
          <div className="clm-ap-info-item">
            <span className="clm-ap-info-lbl">Status</span>
            <span className={`clm-ap-status clm-pill ${statusClass}`}>{info.status ?? "—"}</span>
          </div>
          <div className="clm-ap-info-item">
            <span className="clm-ap-info-lbl">Amount</span>
            <span className="clm-ap-info-val clm-ap-amt">₹ {info.claimedAmount ?? "—"}</span>
          </div>
        </div>
        <button className="clm-btn clm-btn-navy clm-btn-block" style={{ marginTop: 20 }} onClick={onClose}>
          OK, Got It
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ════════ ICONS ════════ */
const IconBarcode = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/>
    <line x1="7" y1="12" x2="17" y2="12"/>
  </svg>
);
const IconDollar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
  </svg>
);
const IconChart = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
    <path d="M3 3v18h18"/>
    <rect x="7" y="10" width="3" height="7"/>
    <rect x="12" y="6" width="3" height="11"/>
    <rect x="17" y="13" width="3" height="4"/>
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/>
  </svg>
);
const IconPlus = ({ size = 17 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width={size} height={size}>
    <path d="M12 5v14M5 12h14"/>
  </svg>
);
const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
  </svg>
);

/* ════════════════════════════════════════════════════════════
   CONSIGNMENT SECTION
   fetch: GET /fetch-Exp_consignment-details/{consignmentNo}
════════════════════════════════════════════════════════════ */
function ConsignmentSection({ consignments, onAdd, onRemove, scanning }) {
  const [scanValue, setScanValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 300); }, []);

  const handleAdd = () => {
    onAdd(scanValue, () => setScanValue(""));
    inputRef.current?.focus();
  };

  const totPcs = consignments.reduce((a, c) => a + (c.pcs || 0), 0);
  const totWt  = consignments.reduce((a, c) => a + (c.wt  || 0), 0);
  const totAmt = consignments.reduce((a, c) => a + (c.amount || 0), 0);

  return (
    <div className="clm-card">
      <div className="clm-band clm-teal-band">
        <div className="clm-band-icon"><IconBarcode /></div>
        <div>
          <div className="clm-band-title">
            <span className="clm-step-num">1</span> Scan Consignments
          </div>
          <div className="clm-band-sub">Enter consignment number to fetch details</div>
        </div>
        <div className="clm-band-r">
          <span className="clm-band-badge">{consignments.length} added</span>
        </div>
      </div>

      <div className="clm-card-body">
        <div className="clm-scan-box">
          <div className="clm-scan-input-wrap">
            <span className="clm-scan-ico">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2"/>
                <line x1="7" y1="12" x2="17" y2="12"/>
              </svg>
            </span>
            <input
              ref={inputRef}
              className="clm-scan-input"
              type="text"
              inputMode="numeric"
              placeholder="Enter consignment number…"
              autoComplete="off"
              spellCheck="false"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              disabled={scanning}
            />
          </div>
          <button className="clm-scan-add-btn" onClick={handleAdd} disabled={scanning}>
            {scanning ? "…" : <IconPlus />}
            {scanning ? "Fetching" : "Add"}
          </button>
        </div>

        {consignments.length === 0 ? (
          <div className="clm-cnt-empty">
            <div className="clm-cnt-empty-ico">📦</div>
            <div className="clm-cnt-empty-title">No consignments yet</div>
            <div className="clm-cnt-empty-sub">Enter a consignment number above to fetch &amp; add</div>
          </div>
        ) : (
          <div className="clm-cnt-grid-wrap">
            <div className="clm-cnt-grid-scroll">
              <table className="clm-cnt-grid">
                <thead>
                  <tr>
                    <th className="clm-c">#</th>
                    <th>Consignment No.</th>
                    <th>From → To</th>
                    <th className="clm-c">Pcs</th>
                    <th className="clm-r">Weight</th>
                    <th className="clm-r">Amt</th>
                    <th className="clm-c"></th>
                  </tr>
                </thead>
                <tbody>
                  {consignments.map((c, i) => (
                    <tr key={c.cnr}>
                      <td className="clm-c"><span className="clm-cg-sn">{i + 1}</span></td>
                      <td><div className="clm-cg-cnr">{c.cnr}</div></td>
                      <td><div className="clm-cg-route">{c.from} → {c.to}</div></td>
                      <td className="clm-c"><span className="clm-cg-pcs">{c.pcs}</span></td>
                      <td className="clm-r"><span className="clm-cg-wt">{c.wt} kg</span></td>
                      <td className="clm-r"><span className="clm-cg-amt">₹ {fmt(c.amount)}</span></td>
                      <td className="clm-c">
                        <button className="clm-cg-remove" onClick={() => onRemove(c.cnr)} aria-label={`Remove ${c.cnr}`}>
                          <IconTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="clm-lbl">
                      Total · {consignments.length} consignment{consignments.length !== 1 ? "s" : ""}
                    </td>
                    <td className="clm-c">{totPcs}</td>
                    <td className="clm-r">{fmt(totWt)} kg</td>
                    <td className="clm-r">₹ {fmt(totAmt)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   EXPENSE LINES SECTION
════════════════════════════════════════════════════════════ */
function ExpenseSection({ expLines, consignments, onAdd, onRemove, onUpdate, onSplitAll }) {
  return (
    <div className="clm-card">
      <div className="clm-band clm-amber-band">
        <div className="clm-band-icon"><IconDollar /></div>
        <div>
          <div className="clm-band-title">
            <span className="clm-step-num">2</span> Expense Heads
          </div>
          <div className="clm-band-sub">Add amount claimed per expense head</div>
        </div>
        <div className="clm-band-r">
          <span className="clm-band-badge">
            {expLines.length} head{expLines.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      <div className="clm-card-body">
        {expLines.map((l, i) => {
          const head = HEADS.find((h) => h.id === l.headId);
          const isAllCons = l.against === "";
          const splitAmt  = isAllCons && consignments.length > 1 && parseFloat(l.amount) > 0
            ? parseFloat(l.amount) / consignments.length
            : null;

          return (
            <div className={`clm-exp-line ${l._error ? "clm-exp-line-error" : ""}`} key={l.uid}>
              <div className="clm-exp-line-top">
                <span className="clm-exp-line-title">
                  {head?.ico || "💰"} Expense {i + 1}
                </span>
                <button className="clm-exp-del" onClick={() => onRemove(l.uid)} aria-label="Delete">
                  <IconTrash />
                </button>
              </div>

              {/* Head + Amount */}
              <div className="clm-exp-amount-row">
                <select
                  className={`clm-exp-head-select ${l._error ? "clm-input-error" : ""}`}
                  value={l.headId}
                  onChange={(e) => onUpdate(l.uid, "headId", Number(e.target.value))}
                >
                  <option value={0}>Select expense head…</option>
                  {HEADS.map((h) => (
                    <option key={h.id} value={h.id}>{h.ico} {h.name}</option>
                  ))}
                </select>
                <div className={`clm-amt-input-wrap ${l._error ? "clm-input-error-wrap" : ""}`}>
                  <span className="clm-amt-prefix">₹</span>
                  <input
                    className="clm-amt-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="1"
                    value={l.amount}
                    placeholder="0"
                    onChange={(e) => onUpdate(l.uid, "amount", e.target.value)}
                  />
                </div>
              </div>

              {/* Against consignment */}
              <div className="clm-exp-against">
                <select
                  value={l.against}
                  onChange={(e) => onUpdate(l.uid, "against", e.target.value)}
                >
                  <option value="">Against: All consignments</option>
                  {consignments.map((c) => (
                    <option key={c.cnr} value={c.cnr}>
                      {c.cnr} ({c.from}→{c.to})
                    </option>
                  ))}
                </select>
              </div>

              {/* Split hint — show when "All consignments" is selected and amount entered */}
              {splitAmt !== null && (
                <div className="clm-split-hint">
                  <span className="clm-split-ico">÷</span>
                  ₹{fmt(parseFloat(l.amount))} ÷ {consignments.length} consignments
                  = <strong>₹{fmt(splitAmt)} each</strong>
                  <button
                    className="clm-split-apply-btn"
                    type="button"
                    onClick={() => onSplitAll(l.uid)}
                  >
                    Apply Split
                  </button>
                </div>
              )}

              {/* Error message */}
              {l._error && (
                <div className="clm-exp-error-msg">⚠ {l._error}</div>
              )}

              {/* Line remark */}
              <div className="clm-exp-against" style={{ marginTop: 8 }}>
                <input
                  type="text"
                  className="clm-exp-head-select"
                  style={{ fontSize: 13 }}
                  placeholder="Line remark (optional)"
                  value={l.remark}
                  onChange={(e) => onUpdate(l.uid, "remark", e.target.value)}
                />
              </div>
            </div>
          );
        })}

        <button className="clm-exp-add-btn" onClick={onAdd}>
          <IconPlus size={16} /> Add Expense Head
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   BUILDER VIEW
   fetch : GET  /fetch-Exp_consignment-details/{consignmentNo}
   save  : POST /submit-claim
════════════════════════════════════════════════════════════ */
function BuilderView({ onSubmitSuccess, showToast }) {
  const [consignments, setConsignments] = useState([]);
  const [expLines, setExpLines]         = useState([]);
  const [expUid, setExpUid]             = useState(1);
  const [claimStatus, setClaimStatus]   = useState("draft");
  const [claimNo, setClaimNo]           = useState("—");
  const [showSubmit, setShowSubmit]     = useState(false);
  const [remark, setRemark]             = useState("");
  const [scanning, setScanning]         = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [pendingRemove, setPendingRemove]   = useState(null);
  const [successPopupNo, setSuccessPopupNo] = useState(null);
  const [claimedPopup, setClaimedPopup]     = useState(null);

  // Read from localStorage — same pattern as Branch.jsx / main shell
  const deliveryBoyId = Number(localStorage.getItem("userId") || 1);
  const createdBy     = localStorage.getItem("username") || "System";
  const branchCodeLS  = localStorage.getItem("branchCode") || null;

  // Live employee/branch lookup — GET /fetch-Emp-name/{userId}
  const [empInfo, setEmpInfo] = useState({ empName: null, branchId: null, branchPrefix: null });

  // Branch / Delivery Boy dropdown data — GET /get-branches, GET /get-delivery-boys
  const [branches, setBranches]           = useState([]);
  const [deliveryBoys, setDeliveryBoys]   = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingBoys, setLoadingBoys]         = useState(true);
  const [selectedBranchId, setSelectedBranchId]   = useState("");
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState("");
  const [defaultsApplied, setDefaultsApplied]     = useState(false);

  /* ── FETCH BRANCH LIST — GET /get-branches ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBranches(true);
      try {
        const res = await api.get("/get-expbranches");
        const list = res.data?.data ?? [];
        if (!cancelled) setBranches(list);
      } catch (err) {
        if (!cancelled) showToast("Failed to load branches", "error");
      } finally {
        if (!cancelled) setLoadingBranches(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── FETCH DELIVERY BOYS — GET /get-delivery-boys?branchId= ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBoys(true);
      try {
        const url = selectedBranchId
          ? `/get-delivery-boys?branchId=${selectedBranchId}`
          : "/get-delivery-boys";
        const res = await api.get(url);
        const list = res.data?.data ?? [];
        if (!cancelled) setDeliveryBoys(list);
      } catch (err) {
        if (!cancelled) showToast("Failed to load delivery boys", "error");
      } finally {
        if (!cancelled) setLoadingBoys(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBranchId]);

  /* ── Once the logged-in employee's own branch/code arrives, pre-select
        it as the default (user can still change it) ── */
  useEffect(() => {
    if (!defaultsApplied && empInfo.branchId) {
      setSelectedBranchId(String(empInfo.branchId));
      if (empInfo.empCode) setSelectedDeliveryBoy(String(empInfo.empCode));
      setDefaultsApplied(true);
    }
  }, [empInfo, defaultsApplied]);

  const handleBranchChange = (val) => {
    setSelectedBranchId(val);
    setSelectedDeliveryBoy(""); // branch changed — force re-pick of delivery boy
  };

  const selectedBranchObj = branches.find((b) => String(b.branchId) === String(selectedBranchId));

useEffect(() => {
  let cancelled = false;

  (async () => {
    try {
      const res = await api.get("/fetch-Emp-name");
      const d = res.data?.data;

      if (!cancelled && d) {
        setEmpInfo({
          empName: d.emp_Name ?? null,
          branchId: d.branch_id ?? null,
          empCode: d.emp_Code ?? null,
          branchPrefix: d.branch_Prefix ?? null,
        });
      }
    } catch (err) {
      console.error("Fetch employee failed:", err);
    }
  })();

  return () => {
    cancelled = true;
  };
}, []);

  // Prefer the user's dropdown selection, fall back to live DB values / localStorage
  const selectedBoyObj = deliveryBoys.find((b) => String(b.empCode) === String(selectedDeliveryBoy));
  const displayName = selectedBoyObj?.empName || empInfo.empName || createdBy;
  const branchCode  = selectedBranchObj?.branchPrefix || empInfo.branchPrefix || branchCodeLS;
  const branchId    = selectedBranchObj?.branchId || empInfo.branchId || 0;
  const empCode     = selectedBoyObj?.empCode || empInfo.empCode || null;

  const claimDate = new Date().toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });

  /* ── FETCH CONSIGNMENT ── */
const addConsignment = async (raw, clearInput) => {
    const cnr = raw.trim();
    if (!cnr) { showToast("Enter a consignment number", "error"); return; }
    if (!/^\d+$/.test(cnr)) { showToast("Consignment number must be numeric", "error"); return; }
    if (consignments.find((c) => c.cnr === cnr)) {
      showToast(cnr + " already added", "error");
      clearInput?.();
      return;
    }

    setScanning(true);
    try {
      const res = await api.get(`/fetch-Exp_consignment-details/${cnr}`);
      const d = res.data;

      // ── Already claimed — show popup ──
      if (d.alreadyClaimed) {
        setClaimedPopup({ cnr, ...d.claimInfo });
        clearInput?.();
        return;
      }

      if (!d.data) { showToast("No data returned for " + cnr, "error"); return; }

      setConsignments((prev) => [...prev, {
        cnr:    String(d.data.consignment_No ?? cnr),
        from:   d.data.booking_Place  ?? "—",
        to:     d.data.delivery_Place ?? "—",
        pcs:    d.data.no_of_pcs      ?? 0,
        wt:     parseFloat(d.data.weight  ?? 0),
        amount: parseFloat(d.data.rate    ?? 0),
        status: "Delivered",
      }]);
      clearInput?.();
      showToast("Consignment " + cnr + " added");

      if (expLines.length === 0) addExpenseLine();

    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Consignment not found";
      showToast(msg, "error");
    } finally {
      setScanning(false);
    }
};
  /* ── REMOVE CONSIGNMENT (with confirm overlay) ── */
  const requestRemove = (cnr) => {
    setPendingRemove({ cnr, msg: `Remove consignment "${cnr}" from this claim?` });
  };
  const confirmRemove = () => {
    const { cnr } = pendingRemove;
    setConsignments((prev) => prev.filter((c) => c.cnr !== cnr));
    setExpLines((prev) => prev.map((l) => l.against === cnr ? { ...l, against: "" } : l));
    setPendingRemove(null);
  };

  /* ── EXPENSE LINE ACTIONS ── */
  const addExpenseLine = () => {
    setExpLines((prev) => [...prev, { uid: expUid, headId: 0, amount: "", remark: "", against: "" }]);
    setExpUid((u) => u + 1);
  };
  const removeExpenseLine = (uid) => setExpLines((prev) => prev.filter((l) => l.uid !== uid));
  const updateExpLine = (uid, field, val) =>
    setExpLines((prev) => prev.map((l) => l.uid === uid ? { ...l, [field]: val, _error: undefined } : l));

  /* ── SPLIT AMOUNT across all consignments ── */
  const splitAll = (uid) => {
    const line = expLines.find((l) => l.uid === uid);
    if (!line || !consignments.length) return;
    const total  = parseFloat(line.amount);
    if (!total || total <= 0) return;
    const perCns = Math.round((total / consignments.length) * 100) / 100;
    // Replace this line with one line per consignment, same head/remark
    setExpLines((prev) => {
      const idx   = prev.findIndex((l) => l.uid === uid);
      const newLines = consignments.map((c, i) => ({
        uid:     expUid + i,
        headId:  line.headId,
        amount:  String(perCns),
        remark:  line.remark,
        against: c.cnr,
        _error:  undefined,
      }));
      const result = [...prev];
      result.splice(idx, 1, ...newLines);
      return result;
    });
    setExpUid((u) => u + consignments.length);
  };

  /* ── TOTALS ── */
  const total      = expLines.reduce((a, l) => a + (parseFloat(l.amount) || 0), 0);
  const validHeads = expLines.filter((l) => l.headId > 0 && parseFloat(l.amount) > 0);
  const showBreak  = consignments.length > 0 && validHeads.length > 0;
  const byHead = {};
  validHeads.forEach((l) => { byHead[l.headId] = (byHead[l.headId] || 0) + parseFloat(l.amount); });

  /* ── VALIDATE ── */
  const validate = () => {
    if (!selectedBranchId) {
      showToast("Select a branch", "error");
      return false;
    }
    if (!selectedDeliveryBoy) {
      showToast("Select a delivery boy", "error");
      return false;
    }
    if (!consignments.length) {
      showToast("Add at least one consignment", "error");
      return false;
    }
    if (!validHeads.length) {
      showToast("Add at least one expense head with amount", "error");
      return false;
    }

    // ── Every consignment must have at least one expense line covering it ──
    // A line covers a consignment if: against === "" (all) OR against === c.cnr
    const uncovered = consignments.filter((c) =>
      !validHeads.some((l) => l.against === "" || l.against === c.cnr)
    );

    if (uncovered.length > 0) {
      // Mark lines with no head selected as errors to guide user
      setExpLines((prev) =>
        prev.map((l) =>
          l.headId === 0 || !(parseFloat(l.amount) > 0)
            ? { ...l, _error: "Select a head and enter amount" }
            : l
        )
      );
      showToast(
        `${uncovered.length} consignment${uncovered.length > 1 ? "s" : ""} have no expense claimed: ${uncovered.map((c) => c.cnr).join(", ")}`,
        "error"
      );
      return false;
    }

    // ── Clear any previous errors ──
    setExpLines((prev) => prev.map((l) => ({ ...l, _error: undefined })));
    return true;
  };

  /* ── SUBMIT ── */
  const confirmSubmit = async () => {
    setShowSubmit(false);
    setSubmitting(true);

    // payload matches SubmitClaimRequest DTO exactly
    const payload = {
      deliveryBoyId: empCode,
      branchCode:  branchCode,
      branchID:     branchId,
      claimDate:   todayISO(),
      claimRemark: remark || null,
      createdBy,
      vehicleNo:   null,
      tripNo:      null,
      tripDate:    null,
      consignments: consignments.map((c) => ({
        consignmentNo:  c.cnr,
        fromPlace:      c.from,
        toPlace:        c.to,
        pcs:            c.pcs,
        weightKg:       c.wt,
        freightAmount:  c.amount,
        deliveryStatus: c.status ?? "Delivered",
      })),
      details: validHeads.map((l) => ({
        expenseHeadId:        l.headId,
        amount:               parseFloat(l.amount),
        againstConsignmentNo: l.against || null,
        remark:               l.remark  || null,
        hasBill:              false,
      })),
    };

    try {
      // matches controller: POST /submit-claim
      const res = await api.post("/submit-claim", payload);
      const d   = res.data;
      setClaimNo(d.claimNo ?? "—");
      setClaimStatus("pending");
      setSuccessPopupNo(d.claimNo ?? "—");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Submit failed";
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = { draft: "● Draft", pending: "● Pending Approval" }[claimStatus] || "● Draft";

  const handleSuccessClose = () => {
    const savedNo = successPopupNo;
    setSuccessPopupNo(null);
    onSubmitSuccess({
      claimId:          null,
      claimNo:          savedNo,
      claimDate:        todayISO(),
      claimedAmount:    total,
      status:           "Pending",
      consignmentCount: consignments.length,
      detailCount:      validHeads.length,
      claimRemark:      remark || null,
    });
  };

  return (
    <>
      {/* Claim Banner */}
      <div className="clm-claim-banner">
        <div className="clm-cb-top">
          <div className="clm-cb-top-title">New Expense Claim</div>
          <span className={`clm-cb-status clm-${claimStatus}`}>{statusLabel}</span>
        </div>
        <div className="clm-cb-meta">
          <div className="clm-cb-meta-item">
            <div className="clm-cb-meta-lbl">Branch</div>
            <AutoComplete
              variant="dark"
              options={branches.map((b) => ({
                value: b.branchId,
                label: `${b.branchName}${b.branchPrefix ? ` (${b.branchPrefix})` : ""}`,
              }))}
              value={selectedBranchId}
              onChange={handleBranchChange}
              placeholder="Select branch…"
              disabled={loadingBranches}
              inputId="clm-banner-branch-input"
            />
          </div>
          <div className="clm-cb-meta-item">
            <div className="clm-cb-meta-lbl">Delivery Boy</div>
            <AutoComplete
              variant="dark"
              options={deliveryBoys.map((b) => ({ value: b.empCode, label: b.empName }))}
              value={selectedDeliveryBoy}
              onChange={setSelectedDeliveryBoy}
              placeholder="Select delivery boy…"
              disabled={loadingBoys}
              inputId="clm-banner-boy-input"
            />
          </div>
          <div className="clm-cb-meta-item">
            <div className="clm-cb-meta-lbl">Date</div>
            <div className="clm-cb-meta-val">{claimDate}</div>
          </div>
        </div>
      </div>

      {/* Step 1 */}
      <ConsignmentSection
        consignments={consignments}
        onAdd={addConsignment}
        onRemove={requestRemove}
        scanning={scanning}
      />

      {/* Step 2 */}
      {consignments.length > 0 && (
        <ExpenseSection
          expLines={expLines}
          consignments={consignments}
          onAdd={addExpenseLine}
          onRemove={removeExpenseLine}
          onUpdate={updateExpLine}
          onSplitAll={splitAll}
        />
      )}

      {/* Breakdown */}
      {showBreak && (
        <div className="clm-card">
          <div className="clm-band clm-navy-band">
            <div className="clm-band-icon"><IconChart /></div>
            <div>
              <div className="clm-band-title">Claim Breakdown</div>
              <div className="clm-band-sub">By expense head</div>
            </div>
          </div>
          <div className="clm-card-body">
            <div className="clm-cat-break">
              {Object.keys(byHead).map((hid) => {
                const h = HEADS.find((x) => x.id === Number(hid));
                return (
                  <div className="clm-cat-row" key={hid}>
                    <span className="clm-cat-row-l">
                      <span className="clm-cat-ico">{h?.ico}</span>
                      {h?.name}
                    </span>
                    <span className="clm-cat-amt">₹ {fmt(byHead[hid])}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Total Card */}
      {showBreak && (
        <div className="clm-total-card">
          <div className="clm-tc-row">
            <span className="clm-tc-lbl">Expense Heads</span>
            <span className="clm-tc-val">{validHeads.length}</span>
          </div>
          <div className="clm-tc-row" style={{ marginTop: 6 }}>
            <span className="clm-tc-lbl">Consignments Covered</span>
            <span className="clm-tc-val">{consignments.length}</span>
          </div>
          <div className="clm-tc-divider" />
          <div className="clm-tc-row">
            <span className="clm-tc-total-lbl">Total Claim Amount</span>
            <span className="clm-tc-total-val">₹ {fmt(total)}</span>
          </div>
          <div className="clm-tc-words">{numWords(Math.round(total))}</div>
        </div>
      )}

      {/* Trip Remark */}
      {consignments.length > 0 && (
        <div className="clm-card">
          <div className="clm-card-body">
            <div className="clm-field">
              <label>
                Trip / Claim Remark{" "}
                <span style={{ color: "var(--clm-muted)", fontWeight: 600, textTransform: "none" }}>
                  (optional)
                </span>
              </label>
              <textarea
                className="clm-claim-remark"
                rows={2}
                placeholder="e.g. Bhiwandi → Latur delivery run, 05 Jun"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sticky Submit Bar */}
      <div className="clm-submit-bar">
        <div className="clm-submit-bar-inner">
          <div className="clm-sb-total">
            <div className="clm-sb-total-lbl">Total Claim</div>
            <div className="clm-sb-total-val">₹ {fmt(total)}</div>
          </div>
          <button
            className="clm-btn clm-btn-green clm-sb-btn"
            onClick={() => validate() && setShowSubmit(true)}
            disabled={submitting}
          >
            <IconSend />
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>

      {/* Portals */}
      {showSubmit && (
        <SubmitSheet total={total} onConfirm={confirmSubmit} onClose={() => setShowSubmit(false)} />
      )}
      <SuccessPopup claimNo={successPopupNo} onClose={handleSuccessClose} />
      <AlreadyClaimedPopup info={claimedPopup} onClose={() => setClaimedPopup(null)} />
      <DeleteOverlay
        pending={pendingRemove}
        onConfirm={confirmRemove}
        onClose={() => setPendingRemove(null)}
      />
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   LIST VIEW
   fetch: GET /get-all-claims?deliveryBoyId=&branchCode=&fromDate=&toDate=
   (branch/delivery-boy/date-period filters — all optional)
════════════════════════════════════════════════════════════ */
function ListView({ onNew, showToast }) {
  const [claims, setClaims]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage]   = useState(1);
  const itemsPerPage = 8;

  // Branch / Delivery Boy / Period filters — GET /get-branches, /get-delivery-boys, /get-all-claims
  const [branches, setBranches]         = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingBoys, setLoadingBoys]         = useState(true);
  const [branchFilter, setBranchFilter]       = useState("");
  const [boyFilter, setBoyFilter]             = useState("");
  const [fromDate, setFromDate]               = useState("");
  const [toDate, setToDate]                   = useState("");

  /* ── FETCH BRANCH LIST — GET /get-branches ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBranches(true);
      try {
        const res = await api.get("/get-expbranches");
        if (!cancelled) setBranches(res.data?.data ?? []);
      } catch (err) {
        if (!cancelled) showToast("Failed to load branches", "error");
      } finally {
        if (!cancelled) setLoadingBranches(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* ── FETCH DELIVERY BOYS — GET /get-delivery-boys?branchId= ── */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingBoys(true);
      try {
        const url = branchFilter
          ? `/get-delivery-boys?branchId=${branchFilter}`
          : "/get-delivery-boys";
        const res = await api.get(url);
        if (!cancelled) setDeliveryBoys(res.data?.data ?? []);
      } catch (err) {
        if (!cancelled) showToast("Failed to load delivery boys", "error");
      } finally {
        if (!cancelled) setLoadingBoys(false);
      }
    })();
    return () => { cancelled = true; };
  }, [branchFilter]);

  const handleBranchFilterChange = (val) => {
    setBranchFilter(val);
    setBoyFilter(""); // branch changed — clear delivery-boy pick so the list re-scopes
    setCurrentPage(1);
  };

  /* ── FETCH CLAIMS — GET /get-all-claims (supports branch/boy/date filters) ── */
  useEffect(() => { fetchClaims(); }, [branchFilter, boyFilter, fromDate, toDate]);

  const fetchClaims = async () => {
    setLoading(true);
    try {
      // matches controller: GET /get-all-claims?deliveryBoyId=&branchCode=&fromDate=&toDate=
      const params = new URLSearchParams();
      if (boyFilter)    params.set("deliveryBoyId", boyFilter);
      if (branchFilter) params.set("branchCode", branchFilter); // controller's branchCode param = BranchID (int)
      if (fromDate && toDate) {
        params.set("fromDate", fromDate);
        params.set("toDate", toDate);
      }
      const qs  = params.toString();
      const res = await api.get(`/get-all-claims${qs ? `?${qs}` : ""}`);
      setClaims(res.data?.data ?? res.data ?? []);
    } catch (err) {
      showToast("Failed to load claims", "error");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setBranchFilter("");
    setBoyFilter("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };
  const filtersActive = branchFilter || boyFilter || fromDate || toDate;

  const STATUS_LABELS = {
    Draft: "Draft", Pending: "⏳ Pending",
    Approved: "✓ Approved", Rejected: "✕ Rejected", Paid: "₹ Paid",
  };
  const STATUS_CLASS = {
    Draft: "clm-pill-draft", Pending: "clm-pill-pending",
    Approved: "clm-pill-approved", Rejected: "clm-pill-rejected", Paid: "clm-pill-paid",
  };

  const filtered = claims.filter((c) => {
    const text = [c.claimNo, c.claimRemark, c.status].join(" ").toLowerCase();
    const matchQ = !search || text.includes(search.toLowerCase());
    const matchS = statusFilter === "all" || c.status?.toLowerCase() === statusFilter;
    return matchQ && matchS;
  });

  const totalPages    = Math.ceil(filtered.length / itemsPerPage);
  const paginated     = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totAmt        = claims.reduce((a, c) => a + (c.claimedAmount ?? 0), 0);
  const pendCount     = claims.filter((c) => c.status === "Pending").length;
  const apprAmt       = claims
    .filter((c) => c.status === "Approved" || c.status === "Paid")
    .reduce((a, c) => a + (c.claimedAmount ?? 0), 0);

  const FILTERS = [
    { key: "all",      label: "All",      count: claims.length },
    { key: "pending",  label: "Pending",  count: claims.filter((c) => c.status === "Pending").length },
    { key: "approved", label: "Approved", count: claims.filter((c) => c.status === "Approved").length },
    { key: "rejected", label: "Rejected", count: claims.filter((c) => c.status === "Rejected").length },
    { key: "paid",     label: "Paid",     count: claims.filter((c) => c.status === "Paid").length },
  ];

  return (
    <>
      {/* Stats */}
      <div className="clm-stat-row">
        <div className="clm-stat-box clm-teal">
          <div className="clm-sb-val2">₹{fmtK(totAmt)}</div>
          <div className="clm-sb-lbl2">Total Claimed</div>
        </div>
        <div className="clm-stat-box clm-amber">
          <div className="clm-sb-val2">{pendCount}</div>
          <div className="clm-sb-lbl2">Pending Approval</div>
        </div>
        <div className="clm-stat-box clm-green">
          <div className="clm-sb-val2">₹{fmtK(apprAmt)}</div>
          <div className="clm-sb-lbl2">Approved</div>
        </div>
        <div className="clm-stat-box clm-navy">
          <div className="clm-sb-val2">{claims.length}</div>
          <div className="clm-sb-lbl2">Total Claims</div>
        </div>
      </div>

      {/* Branch / Delivery Boy / Period filters */}
      <div className="clm-filters-bar" style={filtersBarStyle}>
        <div style={filterFieldStyle}>
          <label style={filterLabelStyle}>Branch</label>
          <AutoComplete
            variant="light"
            options={branches.map((b) => ({
              value: b.branchId,
              label: `${b.branchName}${b.branchPrefix ? ` (${b.branchPrefix})` : ""}`,
            }))}
            value={branchFilter}
            onChange={handleBranchFilterChange}
            placeholder="All Branches"
            disabled={loadingBranches}
            inputId="clm-filter-branch-input"
          />
        </div>

        <div style={filterFieldStyle}>
          <label style={filterLabelStyle}>Delivery Boy</label>
          <AutoComplete
            variant="light"
            options={deliveryBoys.map((b) => ({ value: b.empCode, label: b.empName }))}
            value={boyFilter}
            onChange={(v) => { setBoyFilter(v); setCurrentPage(1); }}
            placeholder="All Delivery Boys"
            disabled={loadingBoys}
            inputId="clm-filter-boy-input"
          />
        </div>

        <div style={filterFieldStyle}>
          <label style={filterLabelStyle}>From</label>
          <input
            type="date"
            style={filterSelectStyle}
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
          />
        </div>

        <div style={filterFieldStyle}>
          <label style={filterLabelStyle}>To</label>
          <input
            type="date"
            style={filterSelectStyle}
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
          />
        </div>

        {filtersActive && (
          <button type="button" style={clearFilterBtnStyle} onClick={clearFilters}>
            ✕ Clear
          </button>
        )}
      </div>

      {/* Search + filter */}
      <div className="clm-search-row">
        <div className="clm-search-box">
          <span className="clm-search-ico">🔍</span>
          <input
            type="text"
            placeholder="Search claims…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <div className="clm-filter-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`clm-filter-chip ${statusFilter === f.key ? "active" : ""}`}
              onClick={() => { setStatusFilter(f.key); setCurrentPage(1); }}
            >
              {f.label}
              <span className="clm-filter-chip-count">{f.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="clm-cnt-empty">
          <div className="clm-cnt-empty-ico">⏳</div>
          <div className="clm-cnt-empty-title">Loading claims…</div>
        </div>
      ) : paginated.length === 0 ? (
        <div className="clm-cnt-empty">
          <div className="clm-cnt-empty-ico">📋</div>
          <div className="clm-cnt-empty-title">No claims found</div>
          <div className="clm-cnt-empty-sub">
            {claims.length === 0 ? "Submit your first expense claim" : "Try adjusting your search or filter"}
          </div>
        </div>
      ) : (
        paginated.map((c) => (
          <div className="clm-claim-card" key={c.claimId}>
            <div className="clm-cc-top">
              <span className="clm-cc-no">{c.claimNo}</span>
              <span className="clm-cc-amt">₹ {fmt(c.claimedAmount ?? 0)}</span>
            </div>
            <div className="clm-cc-mid">
              <span className="clm-cc-date">{fmtDate(c.claimDate)}</span>
              {c.claimRemark && (
                <span className="clm-cc-boy" style={{ fontSize: 11 }}>· {c.claimRemark}</span>
              )}
            </div>
            <div className="clm-cc-bottom">
              <span className="clm-cc-cnt">
                {c.consignmentCount ?? 0} consignments · {c.detailCount ?? 0} heads
              </span>
              <span className={`clm-pill ${STATUS_CLASS[c.status] ?? "clm-pill-draft"}`}>
                {STATUS_LABELS[c.status] ?? c.status}
              </span>
            </div>
          </div>
        ))
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="clm-pagination">
          <div className="clm-pg-info">
            Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
          </div>
          <div className="clm-pg-btns">
            <button className="clm-pg-btn" disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}>Previous</button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i+1} className={`clm-pg-btn ${currentPage === i+1 ? "active" : ""}`}
                onClick={() => setCurrentPage(i+1)}>{i+1}</button>
            ))}
            <button className="clm-pg-btn" disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}

      {/* FAB */}
      <button className="clm-new-claim-btn" onClick={onNew} aria-label="New claim">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width={26} height={26}>
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>
    </>
  );
}

/* ════════════════════════════════════════════════════════════
   ROOT COMPONENT — same showToast pattern as Branch.jsx
════════════════════════════════════════════════════════════ */
export default function ExpClaims() {
  const [view, setView]   = useState("list");
  const [toast, setToast] = useState({ show: false, msg: "", type: "" });
  const toastTimer        = useRef(null);

  function showToast(msg, type = "success") {
    setToast({ show: true, msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(
      () => setToast((t) => ({ ...t, show: false })),
      2800
    );
  }

  const handleSubmitSuccess = () => {
    setView("list");
  };

  return (
    <div className="clm-main">
      {/* Autocomplete styles for the Branch / Delivery Boy pickers below —
          injected here instead of Claims.css so no CSS-file edit is needed */}
      <style>{AUTOCOMPLETE_STYLES}</style>
      <div className="clm-wrap">

        {/* Page Header */}
        <div className="clm-page-hdr">
          <h1 className="clm-page-title">Expense Claim</h1>
        </div>

        {/* View Toggle */}
        <div className="clm-view-toggle">
          <button
            className={`clm-vt-btn ${view === "list" ? "clm-active" : ""}`}
            onClick={() => setView("list")}
          >
            My Claims
          </button>
          <button
            className={`clm-vt-btn ${view === "builder" ? "clm-active" : ""}`}
            onClick={() => setView("builder")}
          >
            New Claim
          </button>
        </div>

        {view === "builder" ? (
          <BuilderView onSubmitSuccess={handleSubmitSuccess} showToast={showToast} />
        ) : (
          <ListView onNew={() => setView("builder")} showToast={showToast} />
        )}
      </div>

      {/* Portal toast — same as Branch.jsx */}
      <Toast toast={toast} />
    </div>
  );
}
